import { useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useIonViewWillEnter, useIonViewDidEnter, useIonViewWillLeave } from '@ionic/react';
import { useRepositories } from '../../../infrastructure/http/RepositoryProvider';
import type { Device } from '../../../domain/Device';
import type { Site } from '../../../domain/Site';
import type { AlertEvent } from '../../../domain/AlertEvent';
import type { ComponentKind, DeviceComponent } from '../../../domain/Component';
import { detectBrowserLocation } from '../../../infrastructure/browser/geolocation';

const POLL_INTERVAL_MS = 60 * 1000;

export function useDeviceDetail(id: string) {
  const history = useHistory();
  const {
    devices: deviceRepo,
    sites: siteRepo,
    alertEvents: alertEventRepo,
    lots: lotRepo,
  } = useRepositories();

  const [device, setDevice] = useState<Device | null>(null);
  const [sites, setSites] = useState<Site[]>([]);
  // Los lotes del usuario, para mostrar en cuál está la estación.
  const [lots, setLots] = useState<{ id: string; name: string }[]>([]);
  const [alertEvents, setAlertEvents] = useState<AlertEvent[]>([]);
  const [loading, setLoading] = useState(true);
  // El spinner es solo para la primera carga: al volver a la pantalla (o en
  // cada refresco) los datos se actualizan sin mover la página.
  const loadedRef = useRef(false);
  const [error, setError] = useState<string | null>(null);
  const [showRename, setShowRename] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [componentError, setComponentError] = useState<string | null>(null);
  const [locationError, setLocationError] = useState<string | null>(null);
  const [locating, setLocating] = useState(false);

  const load = async () => {
    if (!loadedRef.current) setLoading(true);
    setError(null);
    try {
      const [{ device: found }, { sites: siteList }, { events }, lotsRes] = await Promise.all([
        deviceRepo.get(id),
        siteRepo.list(),
        alertEventRepo.listForDevice(id, 20),
        lotRepo.list().catch(() => ({ lots: [] })),
      ]);
      setDevice(found);
      setSites(siteList);
      setLots(lotsRes.lots.map((l) => ({ id: l.id, name: l.name })));
      setAlertEvents(events);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo cargar la estación.');
    } finally {
      loadedRef.current = true;
      setLoading(false);
    }
  };

  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useIonViewWillEnter(() => {
    load();
  });

  useIonViewDidEnter(() => {
    pollRef.current = setInterval(load, POLL_INTERVAL_MS);
  });

  useIonViewWillLeave(() => {
    if (pollRef.current) clearInterval(pollRef.current);
  });

  const handleToggleFavorite = async () => {
    if (!device) return;
    const previous = device;
    setDevice({ ...device, isFavorite: !device.isFavorite });
    try {
      const { device: updated } = await deviceRepo.setFavorite(device.id, !previous.isFavorite);
      setDevice(updated);
    } catch (err) {
      setDevice(previous);
      setError(err instanceof Error ? err.message : 'No se pudo actualizar el favorito.');
    }
  };

  const handleToggleConnectivityNotifications = async (enabled: boolean) => {
    if (!device) return;
    const previous = device;
    setDevice({ ...device, notifyConnectivity: enabled });
    try {
      const { device: updated } = await deviceRepo.setConnectivityNotifications(device.id, enabled);
      setDevice(updated);
    } catch (err) {
      setDevice(previous);
      setError(err instanceof Error ? err.message : 'No se pudo actualizar el aviso de conexión.');
    }
  };

  const handleRename = async (name: string) => {
    if (!device || !name.trim()) return;
    try {
      const { device: updated } = await deviceRepo.update(device.id, {
        name: name.trim(),
      });
      setDevice(updated);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo renombrar la estación.');
    }
  };

  const handleUpdateSite = async (siteId: string | null) => {
    if (!device) return;
    const previous = device;
    setDevice({ ...device, siteId });
    setLocationError(null);
    try {
      const { device: updated } = await deviceRepo.update(device.id, {
        siteId,
      });
      setDevice(updated);
    } catch (err) {
      setDevice(previous);
      setLocationError(err instanceof Error ? err.message : 'No se pudo cambiar el campo.');
    }
  };

  const handleUpdateLocation = async (lat: number, lng: number) => {
    if (!device) return;
    const previous = device;
    setDevice({ ...device, lat, lng, locationSource: 'manual' });
    setLocationError(null);
    try {
      const { device: updated } = await deviceRepo.update(device.id, {
        lat,
        lng,
      });
      setDevice(updated);
    } catch (err) {
      setDevice(previous);
      setLocationError(err instanceof Error ? err.message : 'No se pudo actualizar la ubicación.');
    }
  };

  // Pensado para cuando estás instalando el equipo: tu celular está al lado.
  const handleUseMyLocation = async () => {
    setLocating(true);
    setLocationError(null);
    try {
      const { lat, lon } = await detectBrowserLocation();
      await handleUpdateLocation(lat, lon);
    } catch (err) {
      setLocationError(err instanceof Error ? err.message : 'No se pudo obtener tu ubicación.');
    } finally {
      setLocating(false);
    }
  };

  const handleClearLocation = async () => {
    if (!device) return;
    const previous = device;
    setDevice({ ...device, lat: null, lng: null, locationSource: null });
    setLocationError(null);
    try {
      const { device: updated } = await deviceRepo.update(device.id, {
        lat: null,
        lng: null,
      });
      setDevice(updated);
    } catch (err) {
      setDevice(previous);
      setLocationError(err instanceof Error ? err.message : 'No se pudo quitar la ubicación.');
    }
  };

  const handleDelete = async () => {
    if (!device) return;
    try {
      await deviceRepo.delete(device.id);
      history.replace('/app/devices');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo eliminar la estación.');
    }
  };

  // --- Lo que tiene enchufado: agregar, renombrar, activar/desactivar, quitar ---

  const withComponents = async (action: () => Promise<unknown>, fallback: string) => {
    if (!device) return;
    setComponentError(null);
    try {
      await action();
      const { device: updated } = await deviceRepo.get(device.id);
      setDevice(updated);
    } catch (err) {
      setComponentError(err instanceof Error ? err.message : fallback);
    }
  };

  const handleAddComponent = (kind: ComponentKind) =>
    withComponents(() => deviceRepo.addComponent(id, { kind }), 'No se pudo agregar.');

  const handleRenameComponent = (component: DeviceComponent, name: string) =>
    withComponents(
      () =>
        deviceRepo.updateComponent(id, component.id, {
          name: name.trim() || null,
        }),
      'No se pudo renombrar.'
    );

  const handleToggleComponent = (component: DeviceComponent, enabled: boolean) =>
    withComponents(() => deviceRepo.updateComponent(id, component.id, { enabled }), 'No se pudo actualizar.');

  const handleRemoveComponent = (component: DeviceComponent) =>
    withComponents(() => deviceRepo.removeComponent(id, component.id), 'No se pudo quitar.');

  return {
    device,
    sites,
    lot: device?.lotId ? (lots.find((l) => l.id === device.lotId) ?? null) : null,
    alertEvents,
    loading,
    error,
    showRename,
    setShowRename,
    showDeleteConfirm,
    setShowDeleteConfirm,
    locationError,
    handleToggleFavorite,
    handleToggleConnectivityNotifications,
    handleRename,
    handleUpdateSite,
    handleUpdateLocation,
    handleUseMyLocation,
    handleClearLocation,
    locating,
    handleDelete,
    componentError,
    handleAddComponent,
    handleRenameComponent,
    handleToggleComponent,
    handleRemoveComponent,
  };
}
